import { type ComponentType } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  BarChart3,
  Briefcase,
  Clock,
  Hash,
  Layers,
  Percent,
  Plus,
  Scale,
  TrendingUp,
  User,
  Users,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { formatEurCompact } from "@/lib/formatters";

/** One row inside a tranche array. The engine reads exactly one of
 *  `min` / `max` plus a numeric `score` and an optional `label`. */
export interface Tranche {
  min?: number;
  max?: number;
  score: number;
  /** Operator-authored caption. The engine ignores it; we keep it on
   *  the wire (round-tripped through onChange) but no longer expose
   *  an editable field — the slider value + operator already say it. */
  label?: string;
}

type ConfigValue = Tranche[] | number | string | Record<string, number>;

interface TrancheConfigEditorProps {
  /** Free-form per-rule config map. Walked field-by-field; the editor
   *  picks the right widget per value shape (array → tranche table,
   *  number → score input, object → keyed map, string → read-only). */
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

// ─── Per-criterion display + slider metadata ─────────────────────────────

interface CriteriaConfig {
  label: string;
  unit: string;
  icon: ComponentType<{ className?: string }>;
  /** Slider domain for the threshold value. */
  sliderMin: number;
  sliderMax: number;
  sliderStep: number;
  /** Renders the threshold for the row header + the under-slider
   *  end-cap labels. Receives the raw stored value. */
  format: (v: number) => string;
}

const CRITERIA_CONFIG: Record<string, CriteriaConfig> = {
  revenue_eur: {
    label: "Revenue",
    unit: "€",
    icon: TrendingUp,
    sliderMin: 0,
    sliderMax: 50_000_000,
    sliderStep: 100_000,
    format: formatEurCompact,
  },
  ebitda_eur: {
    label: "EBITDA",
    unit: "€",
    icon: BarChart3,
    sliderMin: 0,
    sliderMax: 10_000_000,
    sliderStep: 50_000,
    format: formatEurCompact,
  },
  ebitda_margin_pct: {
    label: "EBITDA Margin",
    unit: "%",
    icon: Percent,
    sliderMin: 0,
    sliderMax: 30,
    sliderStep: 0.5,
    format: (v) => `${v}%`,
  },
  net_result_eur: {
    label: "Net Result",
    unit: "€",
    icon: ArrowUpRight,
    sliderMin: 0,
    sliderMax: 5_000_000,
    sliderStep: 50_000,
    format: formatEurCompact,
  },
  fonds_propres_eur: {
    label: "Equity",
    unit: "€",
    icon: TrendingUp,
    sliderMin: 0,
    sliderMax: 10_000_000,
    sliderStep: 50_000,
    format: formatEurCompact,
  },
  leverage_ratio: {
    label: "Debt Leverage",
    unit: "x",
    icon: Scale,
    sliderMin: 0,
    sliderMax: 10,
    sliderStep: 0.5,
    format: (v) => `${v.toFixed(1)}x`,
  },
  age: {
    label: "Director Age",
    unit: "years",
    icon: User,
    sliderMin: 30,
    sliderMax: 100,
    sliderStep: 1,
    format: (v) => `${v} years`,
  },
  age_bonus_70plus: {
    label: "Bonus age 70+",
    unit: "pts",
    icon: Award,
    sliderMin: 0,
    sliderMax: 0,
    sliderStep: 1,
    format: (v) => `${v} pts`,
  },
  tenure: {
    label: "Director Tenure",
    unit: "years",
    icon: Clock,
    sliderMin: 0,
    sliderMax: 50,
    sliderStep: 1,
    format: (v) => `${v} years`,
  },
  family_succession: {
    label: "Family Succession",
    unit: "",
    icon: Users,
    sliderMin: 0,
    sliderMax: 0,
    sliderStep: 1,
    format: (v) => String(v),
  },
  management_structure: {
    label: "Management Structure",
    unit: "",
    icon: Briefcase,
    sliderMin: 0,
    sliderMax: 0,
    sliderStep: 1,
    format: (v) => String(v),
  },
};

function configFor(key: string): CriteriaConfig {
  return (
    CRITERIA_CONFIG[key] ?? {
      label: humanize(key),
      unit: "",
      icon: Hash,
      sliderMin: 0,
      sliderMax: 100,
      sliderStep: 1,
      format: (v) => String(v),
    }
  );
}

// ─── Editor entry point ──────────────────────────────────────────────────

export function TrancheConfigEditor({
  config,
  onChange,
}: TrancheConfigEditorProps) {
  function patch(key: string, value: ConfigValue) {
    onChange({ ...config, [key]: value });
  }

  return (
    <div className="mt-3 space-y-5 rounded-xl border border-slate-900/[0.06] bg-slate-50/40 p-5">
      {Object.entries(config).map(([key, value]) => {
        if (Array.isArray(value)) {
          return (
            <TranchesArrayField
              key={key}
              fieldKey={key}
              value={value as Tranche[]}
              onChange={(next) => patch(key, next)}
            />
          );
        }
        if (typeof value === "number") {
          return (
            <ScalarBonusField
              key={key}
              fieldKey={key}
              value={value}
              onChange={(next) => patch(key, next)}
            />
          );
        }
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          const v = value as Record<string, unknown>;
          const isNumericMap = Object.values(v).every(
            (x) => typeof x === "number",
          );
          if (isNumericMap) {
            return (
              <KeyedScoresField
                key={key}
                fieldKey={key}
                value={v as Record<string, number>}
                onChange={(next) => patch(key, next)}
              />
            );
          }
        }
        if (typeof value === "string") {
          return (
            <FlagField key={key} fieldKey={key} value={value} />
          );
        }
        return null;
      })}
    </div>
  );
}

// ─── Tranches (array of {min|max, score, label}) ─────────────────────────

interface TranchesArrayFieldProps {
  fieldKey: string;
  value: Tranche[];
  onChange: (next: Tranche[]) => void;
}

function TranchesArrayField({
  fieldKey,
  value,
  onChange,
}: TranchesArrayFieldProps) {
  const cfg = configFor(fieldKey);
  const Icon = cfg.icon;

  function update(i: number, patch: Tranche) {
    onChange(value.map((t, idx) => (idx === i ? patch : t)));
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...value, { min: cfg.sliderMin, score: 0 }]);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-semibold text-slate-700">
            {cfg.label}
          </span>
          {cfg.unit && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">
              {cfg.unit}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>

      <div className="space-y-1.5 pl-1">
        {value.map((t, i) => (
          <TrancheRow
            key={i}
            tranche={t}
            cfg={cfg}
            onUpdate={(next) => update(i, next)}
            onRemove={() => remove(i)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Tranche row ─────────────────────────────────────────────────────────

interface TrancheRowProps {
  tranche: Tranche;
  cfg: CriteriaConfig;
  onUpdate: (next: Tranche) => void;
  onRemove: () => void;
}

function TrancheRow({ tranche, cfg, onUpdate, onRemove }: TrancheRowProps) {
  const isMin = tranche.min !== undefined;
  const thresholdValue = (isMin ? tranche.min : tranche.max) ?? cfg.sliderMin;
  const isNegative = tranche.score < 0;

  function flipOperator() {
    const carry = thresholdValue;
    const next: Tranche = { score: tranche.score, label: tranche.label };
    if (isMin) next.max = carry;
    else next.min = carry;
    onUpdate(next);
  }

  function setThreshold(v: number) {
    const next: Tranche = { score: tranche.score, label: tranche.label };
    if (isMin) next.min = v;
    else next.max = v;
    onUpdate(next);
  }

  return (
    <div
      className={cn(
        "rounded-xl border px-4 py-3 transition-all",
        isNegative
          ? "border-rose-100 bg-rose-50/50"
          : "border-slate-900/[0.06] bg-slate-50/50",
      )}
    >
      <div className="mb-2 flex items-center gap-3">
        <button
          type="button"
          onClick={flipOperator}
          className="shrink-0 rounded-md border border-slate-200 bg-white px-2.5 py-1 font-mono text-sm transition-colors hover:border-indigo-300"
          aria-label={`Switch to ${isMin ? "≤" : "≥"}`}
        >
          {isMin ? "≥" : "≤"}
        </button>

        <span className="w-24 shrink-0 text-sm font-semibold text-slate-800">
          {cfg.format(thresholdValue)}
        </span>

        <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />

        <Input
          type="number"
          step="0.5"
          value={tranche.score}
          onChange={(e) =>
            onUpdate({
              ...tranche,
              score:
                e.target.value === ""
                  ? 0
                  : parseFloat(e.target.value) || 0,
            })
          }
          className={cn(
            "h-8 w-20 shrink-0 rounded-lg border text-center text-sm font-bold",
            isNegative
              ? "border-rose-200 bg-rose-50 text-rose-600"
              : tranche.score >= 10
                ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                : "border-indigo-200 bg-indigo-50 text-indigo-600",
          )}
        />
        <span className="shrink-0 text-xs text-slate-400">pts</span>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded p-1 text-slate-400 transition-all hover:bg-rose-100 hover:text-rose-500"
          aria-label="Remove tranche"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      <Slider
        min={cfg.sliderMin}
        max={cfg.sliderMax}
        step={cfg.sliderStep}
        value={[clamp(thresholdValue, cfg.sliderMin, cfg.sliderMax)]}
        onValueChange={(arr) => setThreshold(arr[0])}
      />
      <div className="mt-0.5 flex justify-between">
        <span className="text-[9px] text-slate-300">
          {cfg.format(cfg.sliderMin)}
        </span>
        <span className="text-[9px] text-slate-300">
          {cfg.format(cfg.sliderMax)}
        </span>
      </div>
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// ─── Scalar bonus (one number — e.g. age_bonus_70plus) ──────────────────

interface ScalarBonusFieldProps {
  fieldKey: string;
  value: number;
  onChange: (next: number) => void;
}

function ScalarBonusField({
  fieldKey,
  value,
  onChange,
}: ScalarBonusFieldProps) {
  const cfg = configFor(fieldKey);
  const Icon = cfg.icon;
  const isBonus = /bonus/i.test(fieldKey);

  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-xl border px-4 py-2.5",
        isBonus
          ? "border-amber-100 bg-amber-50/50"
          : "border-slate-900/[0.06] bg-slate-50/50",
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={cn(
            "h-4 w-4",
            isBonus ? "text-amber-500" : "text-indigo-400",
          )}
        />
        <span className="text-sm text-slate-700">{cfg.label}</span>
      </div>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.5"
          value={value}
          onChange={(e) =>
            onChange(
              e.target.value === ""
                ? 0
                : parseFloat(e.target.value) || 0,
            )
          }
          className={cn(
            "h-8 w-16 rounded-lg border text-center text-sm font-bold",
            isBonus
              ? "border-amber-200 bg-amber-50 text-amber-600"
              : "border-indigo-200 bg-indigo-50 text-indigo-600",
          )}
        />
        <span className="text-xs text-slate-400">pts</span>
      </div>
    </div>
  );
}

// ─── Keyed scores (e.g. family_succession) ──────────────────────────────

interface KeyedScoresFieldProps {
  fieldKey: string;
  value: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
}

function KeyedScoresField({
  fieldKey,
  value,
  onChange,
}: KeyedScoresFieldProps) {
  const cfg = configFor(fieldKey);
  const Icon = cfg.icon;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-indigo-400" />
        <span className="text-sm font-semibold text-slate-700">
          {cfg.label}
        </span>
      </div>
      <div className="space-y-1.5 pl-1">
        {Object.entries(value).map(([k, v]) => (
          <div
            key={k}
            className="flex items-center justify-between rounded-lg border border-slate-900/[0.06] bg-slate-50 px-4 py-2"
          >
            <span className="text-sm text-slate-600">{humanize(k)}</span>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="1"
                value={v}
                onChange={(e) =>
                  onChange({
                    ...value,
                    [k]:
                      e.target.value === ""
                        ? 0
                        : parseFloat(e.target.value) || 0,
                  })
                }
                className="h-7 w-14 rounded-lg border-indigo-200 bg-indigo-50 text-center text-sm font-bold text-indigo-600"
              />
              <span className="text-xs text-slate-400">pts</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Read-only flag (e.g. evaluation_method = "qualitative") ────────────

function FlagField({
  fieldKey,
  value,
}: {
  fieldKey: string;
  value: string;
}) {
  const cfg = configFor(fieldKey);
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-900/[0.06] bg-slate-50/50 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-slate-400" />
        <span className="text-sm text-slate-700">{cfg.label}</span>
      </div>
      <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
        {value}
      </span>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Maximum cumulative score a single company could earn from a rule's
 *  config map. Tranches inside one criterion are mutually exclusive
 *  (engine returns the first match), so the criterion's contribution
 *  is the *highest* tranche score; criteria across the rule add up.
 *
 *  Used by the rule header to flag a config that overshoots the
 *  rule's max_points cap — operators see a warning when the editable
 *  ceiling is lower than what the rules can hand out. */
export function computeMaxPossibleScore(
  config: Record<string, unknown>,
): number {
  let total = 0;
  for (const value of Object.values(config)) {
    if (Array.isArray(value)) {
      const scores = (value as Tranche[])
        .map((t) => (typeof t.score === "number" ? t.score : 0))
        .filter((n) => n > 0);
      if (scores.length > 0) total += Math.max(...scores);
      continue;
    }
    if (typeof value === "number") {
      if (value > 0) total += value;
      continue;
    }
    if (value && typeof value === "object") {
      const nums = Object.values(value as Record<string, unknown>)
        .filter((x): x is number => typeof x === "number")
        .filter((n) => n > 0);
      if (nums.length > 0) total += Math.max(...nums);
    }
  }
  return total;
}

/** Count of array-shaped (tranche table) entries in the config —
 *  drives the "Max possible: X pts from N criteria" summary line. */
export function countTrancheCriteria(
  config: Record<string, unknown>,
): number {
  return Object.values(config).filter((v) => Array.isArray(v)).length;
}

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
